import type { ApiFlowsExecutionsResponseType } from "@/types/entities/flows";
import { formatDateTime } from "@/utils/dates";
import { getExecutionBadge } from ".";

interface Props {
  execution: ApiFlowsExecutionsResponseType["data"][number]["node_executions"][number];
}

// Mapeo de operaciones de la API a etiquetas legibles
const operationLabels: Record<string, string> = {
  // Operaciones numéricas
  ADD: "Suma",
  SUBTRACT: "Resta",
  MULTIPLY: "Multiplicar",
  DIVIDE: "Dividir",
  POWER: "Potenciar",
  SQRT: "Raíz cuadrada",
  ABS: "Valor absoluto",
  ROUND: "Redondear",
  CEIL: "Redondear hacia arriba",
  FLOOR: "Redondear hacia abajo",
  TRUNCATE: "Truncar",
  LOG10: "Logaritmo",
  LN: "Logaritmo natural",
  EXP: "Exponencial",
  // Operaciones de cadena
  CONCATENATE: "Concatenar",
  REPLACE: "Reemplazar",
  REPLACE_ALL: "Reemplazar todo",
  SLICE: "Extraer texto desde índice",
  TRIM: "Quitar espacios a los extremos",
  TO_UPPER_CASE: "Convertir a mayúsculas",
  TO_LOWER_CASE: "Convertir a minúsculas",
  STRING_LENGTH: "Obtener longitud del texto",
  REPEAT: "Repetir texto",
  SPLIT: "Dividir texto por separador",
  // Operaciones de array
  JOIN: "Unir array",
  ARRAY_LENGTH: "Obtener longitud del array",
  PUSH: "Agregar elemento al final",
  POP: "Eliminar último elemento",
  SHIFT: "Eliminar primer elemento",
  UNSHIFT: "Agregar elemento al inicio",
  REVERSE: "Invertir array",
  SORT: "Ordenar array",
  ARRAY_INCLUDES: "Verificar si incluye elemento",
  FILTER: "Filtrar array",
  MAP: "Mapear array",
  REDUCE: "Reducir array",
  // Operaciones de fecha
  ADD_DAYS: "Agregar días",
  ADD_MONTHS: "Agregar meses",
  ADD_YEARS: "Agregar años",
  SUBTRACT_DAYS: "Restar días",
  SUBTRACT_MONTHS: "Restar meses",
  SUBTRACT_YEARS: "Restar años",
  DATE_DIFF: "Diferencia entre fechas",
  FORMAT_DATE: "Formatear fecha",
  // Operaciones booleanas
  AND: "Y lógico",
  OR: "O lógico",
  NOT: "NO lógico",
};

const CalculatedSimpleContent = ({ execution }: Props) => {
  const calculationInput = execution.inputs.find(
    (input) => input.type === "CALCULATED",
  );
  const result = execution.outputs[0];
  const calculation = calculationInput?.calculations?.[0];

  if (!calculation || !result) {
    return (
      <div className="border border-neutral rounded-lg p-4 bg-neutral-object flex flex-col gap-1">
        <p>No hay datos de cálculo disponibles.</p>
      </div>
    );
  }

  const operationLabel =
    operationLabels[calculation.operation] || calculation.operation;

  // Los inputs de calculation tienen una estructura con pivot.order
  interface CalculationInput {
    value: string | number | boolean | null;
    pivot?: { order?: number };
  }
  const calculationInputs = calculation.inputs as unknown as CalculationInput[];
  const inputValues = calculationInputs
    .sort((a, b) => (a.pivot?.order || 0) - (b.pivot?.order || 0))
    .map((input) => String(input.value));

  return (
    <div className="border border-neutral rounded-lg p-4 bg-neutral-object flex flex-col gap-1">
      <p>Estado del nodo: {getExecutionBadge(execution.status)}</p>
      <p>Se completó el: {formatDateTime(execution.ended_at || "")}</p>

      <div className="mt-2">
        <p className="font-semibold">Operación realizada:</p>
        <p className="pl-4">{operationLabel}</p>
      </div>

      <div className="mt-2">
        <p className="font-semibold">Valores de entrada:</p>
        <div className="pl-4">
          {inputValues.map((value, index) => (
            <p key={index}>
              {index + 1}. {value}
            </p>
          ))}
        </div>
      </div>

      <div className="mt-2">
        <p className="font-semibold">Resultado:</p>
        <p className="pl-4 text-lg font-semibold">{String(result.value)}</p>
      </div>
    </div>
  );
};
export default CalculatedSimpleContent;
